// StoreManagerPage with file upload, download, delete, and RAG-ready support
import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../../helpers/Supabase';
import Loader from '../../Loader/Loader';
import '../../../styles/components/StoreManagerPage.scss';
import { v4 as uuidv4 } from 'uuid';

const ALLOWED_TYPES = ['text/csv', 'text/plain', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

interface User {
  id: string;
  tier: 'free' | 'monthly' | 'annual';
  stripe_customer_id?: string;
  created_at: string;
}

interface PricingTier {
  name: string;
  price: number;
  period: string;
  storeLimit: number;
  features: string[];
  savings?: string;
}

const StoreManagerPage: React.FC = () => {
  const [clientId, setClientId] = useState('');
  const [stores, setStores] = useState<any[]>([]);
  const [filteredStores, setFilteredStores] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showPricingAlert, setShowPricingAlert] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const [storeName, setStoreName] = useState('');
  const [storeZip, setStoreZip] = useState('');
  const [files, setFiles] = useState<FileList | null>(null);

  const [activeStore, setActiveStore] = useState<any | null>(null);
  const [editStoreName, setEditStoreName] = useState('');
  const [editStoreZip, setEditStoreZip] = useState('');
  const [editFiles, setEditFiles] = useState<FileList | null>(null);
  const [existingFiles, setExistingFiles] = useState<any[]>([]);

  const storesPerPage = 10; // Show 10 stores per page

  const pricingTiers: Record<string, PricingTier> = {
    free: {
      name: 'Free Trial',
      price: 0,
      period: 'forever',
      storeLimit: 1,
      features: ['Basic analytics', '1 location', 'Core features']
    },
    monthly: {
      name: 'Monthly Pro',
      price: 60,
      period: '/store/month',
      storeLimit: 999, // Unlimited
      features: ['Unlimited locations', 'AI chatbot access', 'Advanced analytics', 'Priority support']
    },
    annual: {
      name: 'Annual Pro',
      price: 400,
      period: '/store/year',
      storeLimit: 999, // Unlimited
      features: ['Everything in Monthly', '44% savings', 'Early access features', 'VIP support'],
      savings: 'Save 44%'
    }
  };

  const getCurrentTier = (): PricingTier => {
    if (!user || !user.tier || !pricingTiers[user.tier]) {
      return pricingTiers.free;
    }
    return pricingTiers[user.tier];
  };

  const fetchCoordinatesFromZip = async (zip: string) => {
    try {
      // First try with US country code to prioritize US results
      let response = await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${zip}&countrycodes=us&format=json&limit=1`);
      let data = await response.json();
      
      // If no US results, try without country restriction but look for US results
      if (data.length === 0) {
        response = await fetch(`https://nominatim.openstreetmap.org/search?postalcode=${zip}&format=json&limit=5`);
        data = await response.json();
        
        // Look for US results in the expanded results
        const usResult = data.find((result: any) => 
          result.display_name && result.display_name.includes('United States')
        );
        
        if (usResult) {
          return {
            lat: parseFloat(usResult.lat),
            lng: parseFloat(usResult.lon),
          };
        }
      }
      
      // If we have any results, use the first one
      if (data.length > 0) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon),
        };
      }
    } catch (error) {
      console.error("Error fetching coordinates:", error);
    }
    
    // Default to Las Vegas coordinates if all else fails
    return { lat: 36.1147, lng: -115.1728 };
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only initial fetch
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) return;

      const { data: userRows } = await supabase
        .from('users')
        .select('*')
        .eq('auth_user_id', authUser.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const userRow = userRows && userRows.length > 0 ? userRows[0] : null;

      if (userRow) {
        setUser(userRow);
        setClientId(userRow.client_id);
        const { data: storeList } = await supabase
          .from('stores')
          .select('*')
          .eq('client_id', userRow.client_id);

        setStores(storeList || []);
        filterBySearch(storeList || [], searchTerm, showInactive);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchStoreFiles = async (storeId: string) => {
    const { data, error } = await supabase
      .from('store_files')
      .select('*')
      .eq('store_id', storeId);
    if (!error) setExistingFiles(data);
  };

  const deleteFile = async (filePath: string, id: string) => {
    if (!window.confirm("Are you sure you want to delete this file?")) return;
    await supabase.storage.from('store-data').remove([filePath]);
    await supabase.from('store_files').delete().eq('id', id);
    setExistingFiles((prev) => prev.filter(f => f.id !== id));
  };

  const uploadFiles = async (storeId: string, fileList: FileList | null) => {
    if (!fileList) return;

    for (const file of Array.from(fileList)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`File type not allowed: ${file.name}`);
        continue;
      }
      
      const path = `${storeId}/${file.name}`;
      
      try {
        // Get the file as a blob
        const fileBlob = new Blob([file], { type: file.type });
        
        // Get the storage URL and access token
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          console.error('No session found');
          continue;
        }

        const storageUrl = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/store-data/${path}`;
        
        // Upload using direct fetch with proper headers
        const response = await fetch(storageUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': file.type,
            'Cache-Control': '3600',
            'x-upsert': 'true'
          },
          body: fileBlob
        });

        if (response.ok) {
          // Add to database
          await supabase.from('store_files').insert({
            store_id: storeId,
            file_path: path,
            file_type: file.type,
          });
          console.log(`Successfully uploaded: ${file.name}`);
        } else {
          console.error(`Upload failed for ${file.name}:`, response.statusText);
        }
      } catch (error) {
        console.error(`Error uploading ${file.name}:`, error);
      }
    }
  };

  const createStore = async () => {
    if (!storeName || !storeZip) return;

    // Check store limit before creating
    const activeStores = stores.filter(s => s.is_active).length;
    const currentTier = getCurrentTier();

    if (activeStores >= currentTier.storeLimit && currentTier.storeLimit !== 999) {
      setShowPricingAlert(true);
      return;
    }

    const hashId = uuidv4();
    const { lat, lng } = await fetchCoordinatesFromZip(storeZip);

    const { data, error } = await supabase.from('stores').insert([
      {
        store_name: storeName,
        hash_id: hashId,
        client_id: clientId,
        zip: storeZip,
        lat,
        lng,
        is_active: true,
        display_config: {},
      },
    ]).select();

    if (!error && data) {
      const newStore = data[0];
      await uploadFiles(newStore.id, files);
      const updated = [...stores, newStore];
      setStores(updated);
      filterBySearch(updated, searchTerm, showInactive);
      setStoreName('');
      setStoreZip('');
      setFiles(null);
      setShowAddModal(false);
    }
  };

  const updateStore = async () => {
    if (!activeStore) return;
    const { data, error } = await supabase
      .from('stores')
      .upsert({
        id: activeStore.id,
        store_name: editStoreName,
        zip: editStoreZip,
        client_id: activeStore.client_id,
        hash_id: activeStore.hash_id,
        lat: activeStore.lat,
        lng: activeStore.lng,
        is_active: activeStore.is_active,
        display_config: activeStore.display_config,
      }, {
        onConflict: 'id'
      })
      .select();

    if (!error && data) {
      await uploadFiles(activeStore.id, editFiles);
      const updated = stores.map((s) => s.id === activeStore.id ? data[0] : s);
      setStores(updated);
      filterBySearch(updated, searchTerm, showInactive);
      setActiveStore(null);
      setExistingFiles([]);
    }
  };

  const deactivateStore = async (id: string) => {
    if (!user) return;

    const activeStores = stores.filter(s => s.is_active).length;
    const currentTier = getCurrentTier();

    // Check if deactivating would put them under limit
    if (activeStores <= currentTier.storeLimit) {
      alert(`You need at least ${currentTier.storeLimit} active store(s) on your current plan.`);
      return;
    }

    const store = stores.find(s => s.id === id);
    if (!store) return;

    const { error } = await supabase
      .from('stores')
      .upsert({
        id: store.id,
        store_name: store.store_name,
        zip: store.zip,
        client_id: store.client_id,
        hash_id: store.hash_id,
        lat: store.lat,
        lng: store.lng,
        is_active: false,
        display_config: store.display_config,
      }, {
        onConflict: 'id'
      });

    if (!error) {
      const updated = stores.map((s) => s.id === id ? { ...s, is_active: false } : s);
      setStores(updated);
      filterBySearch(updated, searchTerm, showInactive);
    }
  };

  const reactivateStore = async (id: string) => {
    if (!user) return;

    const activeStores = stores.filter(s => s.is_active).length;
    const currentTier = getCurrentTier();

    // Check if activating would exceed limit
    if (activeStores >= currentTier.storeLimit && currentTier.storeLimit !== 999) {
      setShowPricingAlert(true);
      return;
    }

    const store = stores.find(s => s.id === id);
    if (!store) return;

    const { error } = await supabase
      .from('stores')
      .upsert({
        id: store.id,
        store_name: store.store_name,
        zip: store.zip,
        client_id: store.client_id,
        hash_id: store.hash_id,
        lat: store.lat,
        lng: store.lng,
        is_active: true,
        display_config: store.display_config,
      }, {
        onConflict: 'id'
      });

    if (!error) {
      const updated = stores.map((s) => s.id === id ? { ...s, is_active: true } : s);
      setStores(updated);
      filterBySearch(updated, searchTerm, showInactive);
    }
  };

  const filterBySearch = (allStores: any[], term: string, showInactive: boolean) => {
    const filtered = allStores.filter(s =>
      s.store_name.toLowerCase().includes(term.toLowerCase()) &&
      (showInactive || s.is_active)
    );
    setFilteredStores(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const paginateStores = () => {
    const startIndex = (currentPage - 1) * storesPerPage;
    return filteredStores.slice(startIndex, startIndex + storesPerPage);
  };

  const totalPages = Math.ceil(filteredStores.length / storesPerPage);

  if (isLoading) return <Loader />;

  const currentTier = getCurrentTier();
  const activeStores = stores.filter(s => s.is_active).length;

  return (
    <div className="store-manager-page">
      <h1 className="page-title">🧑‍💼 Manage Your Stores</h1>

      <div className="top-bar">
        <button className="add-store-btn" onClick={() => setShowAddModal(true)}>➕ Add Store</button>
        <input
          type="text"
          placeholder="Search stores..."
          value={searchTerm}
          onChange={(e) => {
            const term = e.target.value;
            setSearchTerm(term);
            filterBySearch(stores, term, showInactive);
          }}
          className="search-input"
        />
        <label className="toggle-inactive">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => {
              setShowInactive(e.target.checked);
              filterBySearch(stores, searchTerm, e.target.checked);
            }}
          />
          Show Inactive Stores
        </label>
      </div>

      {/* Store Limit Warning */}
      {activeStores >= currentTier.storeLimit && currentTier.storeLimit !== 999 && (
        <div className="store-limit-warning">
          <span className="warning-icon">⚠️</span>
          <span>You've reached your store limit ({currentTier.storeLimit}) on the {currentTier.name} plan.</span>
          <button onClick={() => setShowPricingAlert(true)} className="upgrade-btn">Upgrade Plan</button>
        </div>
      )}

      <div className="store-stats">
        <p>Showing {paginateStores().length} of {filteredStores.length} store{filteredStores.length !== 1 ? 's' : ''} (Page {currentPage} of {totalPages})</p>
      </div>
      
      <div className="store-grid">
        {paginateStores().map((store) => (
          <div
            key={store.id}
            className={`store-card ${!store.is_active ? 'inactive' : ''}`}
            onClick={() => {
              setActiveStore(store);
              setEditStoreName(store.store_name);
              setEditStoreZip(store.zip);
              fetchStoreFiles(store.id);
            }}
          >
            <h2>{store.store_name}</h2>
            <p><strong>Zip:</strong> {store.zip || 'N/A'}</p>
            <p><a href={`/checkin/${store.hash_id}`} target="_blank" rel="noreferrer">View Check-In</a></p>
            {store.is_active ? (
              <button onClick={(e) => { e.stopPropagation(); deactivateStore(store.id); }}>🗑 Deactivate</button>
            ) : (
              <button 
                onClick={(e) => { e.stopPropagation(); reactivateStore(store.id); }}
                disabled={activeStores >= currentTier.storeLimit && currentTier.storeLimit !== 999}
              >
                ♻️ Reactivate
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="pagination-controls">
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>
          
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                className={`page-btn ${pageNum === currentPage ? 'active' : ''}`}
                onClick={() => setCurrentPage(pageNum)}
              >
                {pageNum}
              </button>
            ))}
          </div>
          
          <button 
            className="pagination-btn"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add New Store</h2>
            <input value={storeName} onChange={e => setStoreName(e.target.value)} placeholder="Store Name" />
            <input value={storeZip} onChange={e => setStoreZip(e.target.value)} placeholder="Zip Code" />
            <label htmlFor="store-files">Upload Store Data (PDF/CSV):</label>
            <input 
              id="store-files"
              type="file" 
              multiple 
              onChange={(e) => setFiles(e.target.files)}
              aria-label="Upload store data files"
            />
            <button onClick={createStore}>Save Store</button>
            <button onClick={() => setShowAddModal(false)}>Cancel</button>
          </div>
        </div>
      )}

      {activeStore && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Edit Store</h2>
            <input value={editStoreName} onChange={(e) => setEditStoreName(e.target.value)} placeholder="Store Name" />
            <input value={editStoreZip} onChange={(e) => setEditStoreZip(e.target.value)} placeholder="Zip Code" />
            <label htmlFor="edit-store-files">Upload Additional Store Data (PDF/CSV):</label>
            <input 
              id="edit-store-files"
              type="file" 
              multiple 
              onChange={(e) => setEditFiles(e.target.files)}
              aria-label="Upload additional store data files"
            />

            <h3>📁 Existing Files</h3>
            <ul className="file-list">
              {existingFiles.map(file => (
                <li key={file.id} className="file-row">
                  <a href={`https://xoulgvjyxtwmbndzbgbi.supabase.co/storage/v1/object/public/store-data/${file.file_path}`} target="_blank" rel="noreferrer">
                    {file.file_path.split('/').pop()}
                  </a>
                  <button onClick={() => deleteFile(file.file_path, file.id)}>🗑 Delete</button>
                </li>
              ))}
            </ul>

            <button onClick={updateStore}>💾 Save Changes</button>
            <button onClick={() => setActiveStore(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Pricing Alert Modal */}
      {showPricingAlert && (
        <div className="modal-overlay">
          <div className="modal-content pricing-alert">
            <div className="alert-header">
              <h3>🚀 Upgrade Your Plan</h3>
              <button 
                onClick={() => setShowPricingAlert(false)}
                className="close-btn"
              >
                ✕
              </button>
            </div>
            <div className="alert-body">
              <p>You've reached your store limit on the {currentTier.name} plan!</p>
              <p>Upgrade to add more stores and unlock additional features.</p>
              
              <div className="upgrade-options">
                <div className="upgrade-option">
                  <h4>Monthly Pro</h4>
                  <div className="price">$60/store/month</div>
                  <button className="upgrade-btn">Upgrade Now</button>
                </div>
                <div className="upgrade-option featured">
                  <h4>Annual Pro</h4>
                  <div className="price">$400/store/year</div>
                  <div className="savings">Save 44%</div>
                  <button className="upgrade-btn">Upgrade Now</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoreManagerPage;
